import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import type { DatovkaApiType, OsobyFyzickaOsobaDto } from '@gov-nx/api';
import {
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useIdentity } from '@gov-nx/auth/common';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import type {
	FormWizardHook,
	PersonCommunicationData,
} from '@gov-nx/core/hooks';
import {
	useCountryCodes,
	useDownloadBlob,
	usePersonCommunicationData,
	usePersonCommunicationForm,
	useRobPersonQuery,
	useWizard,
} from '@gov-nx/core/hooks';
import { getRequiredDataBoxes } from '@gov-nx/core/service';
import type { Nullable, Optional } from '@gov-nx/core/types';
import { useDataBoxAccess } from '@gov-nx/module/data-box';
import { dataBoxParams, phoneShapeToString } from '@gov-nx/utils/common';
import { serviceRouteResolver } from '@gov-nx/web';
import { ServiceCode } from '../../definitions/codes';
import { useKorespondencniVolbyLocale } from '../useKorespondencniVolbyLocale';
import type {
	KorespondencniHlasovaniDokumentyStep1,
	KorespondencniHlasovaniDolumentyFormStep1,
} from './useFormStep1';
import { useFormStep1 } from './useFormStep1';
import type {
	KorespondencniHlasovaniDokumentyFormStep2,
	KorespondencniHlasovaniDokumentyStep2,
} from './useFormStep2';
import { useFormStep2 } from './useFormStep2';
import type {
	KorespondencniHlasovaniDokumentyFormStep3,
	KorespondencniHlasovaniDokumentyStep3,
} from './useFormStep3';
import { useFormStep3 } from './useFormStep3';

interface KorespondencniHlasovaniDokumentyContext {
	wizard: FormWizardHook<DokumentyForm>;
	step1: KorespondencniHlasovaniDolumentyFormStep1;
	step2: KorespondencniHlasovaniDokumentyFormStep2;
	step3: KorespondencniHlasovaniDokumentyFormStep3;
	personCommunicationData: PersonCommunicationData;
	isLoading: boolean;
	isSubmitting: boolean;
	person?: OsobyFyzickaOsobaDto;
	isOnOtherListValue: Nullable<'YES' | 'NO'>;
	pickupMethodSelected: Optional<
		KorespondencniHlasovaniDokumentyStep2['pickUpMethod']
	>;
	powerOfAttorney: {
		download: () => void;
		isDownloading: boolean;
	};
	showConnectDataboxBanner: boolean;
	databoxTypes: DatovkaApiType['typSchranky'][];
	recapKeyValues: { label: string; value: string }[];
	url: string;
}

type DokumentyForm = KorespondencniHlasovaniDokumentyStep1 &
	KorespondencniHlasovaniDokumentyStep2 &
	KorespondencniHlasovaniDokumentyStep3;

const ServiceContext =
	createContext<Nullable<KorespondencniHlasovaniDokumentyContext>>(null);

interface Props {
	children: ReactNode;
	electionId: number;
	onSubmitSuccess: () => void;
}

export const KorespondencniHlasovaniDokumentyContextProvider = ({
	children,
	electionId,
	onSubmitSuccess,
}: Props) => {
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const databoxTypes = getRequiredDataBoxes(
		ServiceCode['zadost-o-korespondencni-hlasovani'],
		application
	);

	const personQuery = useRobPersonQuery();
	const countryCodes = useCountryCodes();
	const { t } = useKorespondencniVolbyLocale();
	const { toastMessageSuccess } = useMessageEvents();
	const { handleDownloadBlob } = useDownloadBlob();
	const { isDataBoxLoginType } = useIdentity();
	const { areAnyConnected } = useDataBoxAccess({
		requiredTypes: databoxTypes,
	});
	const { axiosInstance } = useAxiosInstance();
	const submitMutation = usePoMutation({
		mutationFn: async (variables: DokumentyForm) => {
			const { datovaSchrankaId, ...params } = dataBoxParams(variables);

			return sendSubmissionQuery(axiosInstance)({
				params: {
					odesilatelDSId: datovaSchrankaId,
					...params,
				},
				body: {
					kodPodani: 'ZADOST_KORESPONDENCNI_HLASOVANI',
					zpusobPrevzeti: variables.pickUpMethod,
					zuKod: variables.officeId,
					adresaUlice: variables.street,
					adresaMesto: variables.city,
					adresaPsc: variables.zip,
					adresaStat: variables.country,
					email: variables._useEmail ? variables.email : undefined,
					volbyId: electionId,
					telefon:
						variables._usePhone && variables.phone
							? phoneShapeToString(variables.phone)
							: undefined,
				},
			});
		},
		onSuccess: () => {
			toastMessageSuccess(t('dokumenty.zpravy.odeslano'));
			wizard.resetForms();
			onSubmitSuccess();
		},
	});

	const isSubmitting = submitMutation.isLoading;

	const personCommunicationData = usePersonCommunicationData();

	const step1 = useFormStep1({ isDisabled: isSubmitting });
	const step2 = useFormStep2({ isDisabled: isSubmitting });
	const step3 = useFormStep3({
		isDisabled: isSubmitting,
		databoxTypes,
	});

	usePersonCommunicationForm(step1.form, {
		countryCodes,
		email: personCommunicationData.emailVerified,
		phone: personCommunicationData.phoneVerified,
	});

	const wizard = useWizard<DokumentyForm>({
		steps: [step1.form, step2.form, step3],
		onSubmit: async (values) => submitMutation.mutate(values),
		onStepInvalid: (index, errors) => {
			if (index === 0) {
				personCommunicationData.onFormValidated(errors);
			}
		},
	});

	const { pickUpMethod, city, zip, street, country } =
		step2.form.formMethods.getValues();
	const recapKeyValues = [
		{
			label: t('dokumenty.rekapitulace.udaje-pro-prevzeti.zastupitelsky-urad'),
			value: `${step2.selectedCountry}, ${step2.selectedDistrict}, ${step2.selectedOffice}`,
		},
		{
			label: t(
				'dokumenty.rekapitulace.udaje-pro-prevzeti.zpusob-prevzeti.klic'
			),
			value: pickUpMethod
				? `${
						{
							POSTA: `${t(
								'dokumenty.rekapitulace.udaje-pro-prevzeti.zpusob-prevzeti.posta'
							)}: ${street}, ${zip}, ${city} , ${country}`,
							OSOBNE: t(
								'dokumenty.rekapitulace.udaje-pro-prevzeti.zpusob-prevzeti.osobne'
							),
						}[pickUpMethod]
					}`
				: '',
		},
	];

	return (
		<ServiceContext.Provider
			value={{
				isLoading:
					personQuery.isLoading || step2.isLoading || countryCodes.isLoading,
				wizard,
				personCommunicationData,
				step1: step1.form,
				step2: step2.form,
				step3,
				isSubmitting,
				isOnOtherListValue: step1.isOnOtherListValue,
				pickupMethodSelected: step2.pickUpMethod,
				person: personQuery.data,
				powerOfAttorney: handleDownloadBlob(() =>
					prepareToDownloadPowerOfAttorney(
						step2.selectedOffice,
						step2.selectedCountry
					)
				),
				showConnectDataboxBanner: isDataBoxLoginType && !areAnyConnected,
				databoxTypes,
				recapKeyValues,
				url: [
					serviceRouteResolver('zadost-o-korespondencni-hlasovani'),
					'zapis',
				].join('/'),
			}}>
			{children}
		</ServiceContext.Provider>
	);
};

const prepareToDownloadPowerOfAttorney = (
	selectedOffice?: string,
	selectedCountry?: string
) => ({
	body: {
		kodPodani: 'ZADOST_KORESPONDENCNI_HLASOVANI_PLNA_MOC',
		zuNazev: selectedOffice,
		zuStat: selectedCountry,
	},
});

export const useKorespondencniHlasovaniDokumentyContext = () => {
	const context = useContext(ServiceContext);
	if (!context) {
		throw new Error('Context must be used within a ContextProvider');
	}
	return context;
};
